import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Users, Clock, ArrowRight } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { SimpleLineChart } from './SimpleLineChart';
import { SectionTitle } from './SectionTitle';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { PsychologistMetrics } from '../../../../hooks/api/psychologist/metrics';
import { usePsychologistContext } from '../../../../contexts/PsychologistContext';

// ── Mock data (fallback cuando el backend todavía no envía estos campos) ───

const cancelacionesData = [
  { mes: 'Nov', valor: 10 },
  { mes: 'Dic', valor: 11 },
  { mes: 'Ene', valor: 13 },
  { mes: 'Feb', valor: 12 },
  { mes: 'Mar', valor: 7 },
  { mes: 'Abr', valor: 16 },
];

const reprogramacionesData = [
  { mes: 'Nov', valor: 22 },
  { mes: 'Dic', valor: 25 },
  { mes: 'Ene', valor: 20 },
  { mes: 'Feb', valor: 24 },
  { mes: 'Mar', valor: 27 },
  { mes: 'Abr', valor: 18 },
];

const tooltipStyle = { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' };

interface Props {
  metrics: PsychologistMetrics | null;
  loading: boolean;
}

export const MetricsResumenTab: React.FC<Props> = ({ metrics, loading }) => {
  const val = (n: number | undefined) => (loading ? '—' : String(n ?? 0));
  const pct = (n: number | undefined) => (loading ? '—' : `${n ?? 0}%`);
  const { psychologist } = usePsychologistContext();

  const procesosNuevosData = (metrics?.monthly_new_processes ?? []).map((b) => ({ mes: b.month, valor: b.value }));
  const procesosInactivosData = (metrics?.monthly_inactive_processes ?? []).map((b) => ({ mes: b.month, valor: b.value }));
  const derivacionesData = (metrics?.monthly_derivations ?? []).map((b) => ({ mes: b.month, valor: b.value }));

  const cancelacionesChart = (metrics?.monthly_cancelled_pct ?? cancelacionesData).map((b) =>
    'mes' in b ? b : { mes: b.month, valor: b.value },
  );
  const reprogramacionesChart = (metrics?.monthly_rescheduled_pct ?? reprogramacionesData).map((b) =>
    'mes' in b ? b : { mes: b.month, valor: b.value },
  );

  const retencionRows = [
    { label: 'Sesiones promedio por proceso', tuValor: val(metrics?.avg_sessions_per_process) },
    { label: 'Promedio de días entre sesiones', tuValor: loading ? '—' : `${metrics?.avg_days_between_sessions ?? 0} días` },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Cards de resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={<Users size={16} className="text-gray-500" />}
          value={val(metrics?.active_processes)}
          label="Procesos activos"
          subLabel="Con sesiones en últimos 30 días"
          tooltipText="Cantidad de procesos con al menos una sesión en los últimos 30 días."
        />
        <MetricCard
          icon={<Users size={16} className="text-green-500" />}
          value={<span className="text-green-600">{pct(metrics?.adherence_pct)}</span>}
          label="% Sesiones realizadas sobre el total de sesiones agendadas"
          tooltipText="Adherencia: mide el compromiso de los pacientes con su proceso terapéutico, comparando las sesiones efectivamente realizadas contra las agendadas en el período."
        />
        {psychologist?.rankingCategory === 'N' && (
          <MetricCard
            icon={<Users size={16} className="text-violet-500" />}
            value={val(metrics?.process_limit)}
            label="Límite de pacientes"
            tooltipText="Límite máximo de pacientes activos permitido para esta psicóloga."
          />
        )}
        <MetricCard
          icon={<Clock size={16} className="text-teal-500" />}
          value={val(metrics?.sessions_next_7_days)}
          label="Sesiones próximos 7 días"
          tooltipText="Cantidad de sesiones agendadas para los próximos 7 días."
        />
        <MetricCard
          icon={<Users size={16} className="text-orange-400" />}
          value={<span className="text-orange-500">{val(metrics?.inactive_processes)}</span>}
          label="Procesos inactivos"
          tooltipText="Procesos sin sesiones registradas en los últimos 30 días."
        />
      </div>

      {/* Gráficos fila */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SimpleLineChart data={procesosNuevosData} color="#7C3AED" title="Procesos Nuevos (por mes)" />
        <SimpleLineChart data={procesosInactivosData} color="#F97316" title="Procesos Inactivos (por mes)" />
      </div>

      {/* Gráfico full width */}
      <SimpleLineChart data={derivacionesData} color="#3B82F6" title="Derivaciones (por mes)" />

      {/* Derivaciones */}
      <section>
        <SectionTitle
          title="Derivaciones"
          tooltipText="Resumen histórico de derivaciones de la psicóloga."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MetricCard
            icon={<ArrowRight size={16} className="text-blue-500" />}
            value={val(metrics?.derivations_by_patient)}
            label="Deriv. del paciente"
            tooltipText="Derivaciones solicitadas por el paciente."
          />
          <MetricCard
            icon={<ArrowRight size={16} className="text-violet-500" />}
            value={val(metrics?.derivations_requested)}
            label="Deriv. solicitadas"
            tooltipText="Derivaciones iniciadas por la psicóloga."
          />
        </div>
      </section>

      {/* Sesiones */}
      <section>
        <SectionTitle
          title="Sesiones"
          tooltipText="Métricas de sesiones realizadas en el período actual y total histórico."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-2xl font-bold text-gray-900">{val(metrics?.sessions_this_month)}</p>
            <p className="text-sm text-gray-500 mt-0.5">Sesiones este mes</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-2xl font-bold text-gray-900">{loading ? '—' : (metrics?.sessions_total ?? 0).toLocaleString('es-AR')}</p>
            <p className="text-sm text-gray-500 mt-0.5">Sesiones totales</p>
          </div>
        </div>
      </section>

      {/* Cancelaciones y reprogramaciones */}
      <section>
        <SectionTitle
          title="Cancelaciones y reprogramaciones"
          tooltipText="Porcentaje de sesiones canceladas y reprogramadas en el mes actual y evolución histórica."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-1.5">
              <p className="text-2xl font-bold text-red-500">{pct(metrics?.cancelled_pct_this_month)}</p>
              <InfoTooltip text="Se refiere al mes calendario en curso." placement="top" ariaLabel="Info % Cancelaciones del mes corriente" />
            </div>
            <p className="text-sm text-gray-500 mt-0.5">% Cancelaciones del mes corriente</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-1.5">
              <p className="text-2xl font-bold text-orange-400">{pct(metrics?.rescheduled_pct_this_month)}</p>
              <InfoTooltip text="Se refiere al mes calendario en curso." placement="top" ariaLabel="Info % Reprogramaciones del mes corriente" />
            </div>
            <p className="text-sm text-gray-500 mt-0.5">% Reprogramaciones del mes corriente</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">% Cancelaciones por mes</p>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={cancelacionesChart} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}%`, 'Cancelaciones']} />
                <Line type="monotone" dataKey="valor" stroke="#EF4444" strokeWidth={2} dot={{ r: 3, fill: '#EF4444', strokeWidth: 0 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">% Reprogramaciones por mes</p>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={reprogramacionesChart} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}%`, 'Reprogramaciones']} />
                <Line type="monotone" dataKey="valor" stroke="#F97316" strokeWidth={2} dot={{ r: 3, fill: '#F97316', strokeWidth: 0 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Retención de pacientes */}
      <section>
        <SectionTitle
          title="Retención de pacientes"
          tooltipText="Indicadores de retención de pacientes de la psicóloga."
        />
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          {retencionRows.map((row, i) => (
            <div key={i} className={`flex items-center justify-between py-2.5 px-1 text-sm ${i > 0 ? 'border-t border-gray-100' : ''}`}>
              <span className="text-gray-600">{row.label}</span>
              <span className="font-semibold text-gray-900">{row.tuValor}</span>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};
